import { useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Icon } from '../index';

import type { NoteEventDraft } from './state/loggerDraftTypes';
import type { useTheme } from './theme';

// A note mini-card in the timeline feed (Solution C). Renders like an exercise
// row — 💬 icon + time + the note text — interleaved by performedAt. Tap to
// expand/collapse the full text (the author must be able to re-read their own
// note — [[feedback_user_visual_accessibility]]); a ✕ removes the draft note
// (draft-local until the workout saves, so no confirm needed in v1). Editing an
// already-saved note is a documented follow-up (delete + retype for now).
export function TimelineNoteCard({
  note,
  time,
  onDelete,
  t,
  theme,
}: {
  note: NoteEventDraft;
  /** Pre-formatted clock time (the list owns the locale Intl formatter). */
  time: string;
  onDelete: (id: string) => void;
  t: (k: string, opts?: Record<string, unknown>) => string;
  theme: ReturnType<typeof useTheme>;
}) {
  const styles = makeStyles(theme);
  const [expanded, setExpanded] = useState(false);

  return (
    <View style={styles.row}>
      {/* styles.icon has no explicit color; using ink2 as neutral secondary — emoji system color unavailable to Icon */}
      <View style={{ marginTop: 1 }}>
        <Icon name="comment" size={theme.typography.sizes.base} color={theme.palette.ink2} />
      </View>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('mobile.timeline.noteExpandA11y', {
          defaultValue: 'Note — tap to read in full',
        })}
        onPress={() => setExpanded((v) => !v)}
        style={styles.body}
      >
        <Text style={styles.time}>{time}</Text>
        <Text
          style={styles.text}
          numberOfLines={expanded ? undefined : 2}
        >
          {note.text}
        </Text>
      </Pressable>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('mobile.timeline.noteDeleteA11y', {
          defaultValue: 'Delete note',
        })}
        onPress={() => onDelete(note.id)}
        hitSlop={8}
        style={({ pressed }) => [styles.delBtn, pressed && styles.delPressed]}
      >
        <Icon name="close" size={theme.typography.sizes.sm} color={theme.palette.ink3} />
      </Pressable>
    </View>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    row: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: s.s3,
      backgroundColor: p.surface2,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r2,
      paddingHorizontal: s.s4,
      paddingVertical: s.s3,
    },
    icon: { fontSize: ty.sizes.base, marginTop: 1 },
    body: { flex: 1, gap: 2 },
    time: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontVariant: ['tabular-nums'],
    },
    text: {
      color: p.ink,
      fontSize: ty.sizes.base,
    },
    delBtn: {
      minWidth: 32,
      minHeight: 32,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: r.r1,
    },
    delPressed: { backgroundColor: p.surface },
    delText: { color: p.ink3, fontSize: ty.sizes.sm, fontWeight: '700' },
  });
}
